import React from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    AlertTitle,
    Box,
    Stack,
    Typography,
    IconButton,
    Tooltip
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import FormDialog from '../../components/dialogs/FormDialog';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
import QRCode from 'react-qr-code';

import { hasError, formatApiErrors } from '../../utils/formatError';
import { centToCurrency } from '../../utils/formatNumber';
import Label from '../../components/label';
import { statusLabelProps } from '../../config/statusTones';
import Iconify from '../../components/iconify';
import i18n from '../../i18n';
import Fields from '../editors';

/** Keys a provider commonly returns a scannable payload under. */
const QR_KEYS = ['qr', 'qrCode', 'qr_code', 'payload'];

export default class CreateTransactionDialog extends React.Component {
    type;
    constructor(props) {
        super(props);
        this.state = {
            dialogData: {},
            alertMessages: [],
            alertTitle: '',
            loading: false,
            link: null,
            result: null,
            copied: false
        };
        this.type = props.type || 'default';
    }

    static buildEmptyData(Head) {
        const data = {};
        if (Array.isArray(Head)) {
            Head.forEach((field) => {
                data[field.id] = 'default' in field ? field.default : '';
            });
        }
        return data;
    }

    componentDidMount() {
        if (this.props.dialogOpen) {
            this.initializeDialogData();
        }
    }
    componentDidUpdate(prevProps) {
        if (!prevProps.dialogOpen && this.props.dialogOpen) {
            this.initializeDialogData();
        }
    }

    initializeDialogData = () => {
        const data = CreateTransactionDialog.buildEmptyData(this.props.Head);
        const hasPrevData =
            this.props.enablePrevData &&
            !!localStorage.getItem(
                `lastDialogData_${this.props.id}_${this.type}`
            );
        this.setState({
            dialogData: data,
            alertMessages: [],
            alertTitle: '',
            loading: false,
            prevDataLoaded: false,
            result: null,
            link: null,
            copied: false,
            hasPrevData
        });
    };

    handleShowLink = (link) => {
        this.setState({ link, alertMessages: [], alertTitle: '' });
    };
    handleClose = () => {
        this.setState({ link: null, result: null, alertMessages: [] });
        this.props.onClose();
    };

    /**
     * Required fields, checked before anything leaves the browser.
     *
     * This dialog is not on react-hook-form (see FormField's docblock), so without
     * this an empty form submits and burns a round trip to come back with the same
     * answer. The rules come off the same Head the fields are built from.
     */
    validate = (dialogData) => {
        const messages = [];
        this.props.Head.forEach((field) => {
            if (field.onlyRead || !field.required) return;
            const value = dialogData[field.id];
            if (value === '' || value === null || value === undefined) {
                messages.push(
                    `${i18n.t(`columns.${field.label}`)}: ${i18n.t('errors.fieldRequired')}`
                );
            }
        });
        return messages;
    };

    handleDialogSave = async () => {
        // The raw form state is what gets cached, so "fill previous data" restores
        // the JSON the operator typed rather than a re-serialized version of it.
        localStorage.setItem(
            `lastDialogData_${this.props.id}_${this.type}`,
            JSON.stringify(this.state.dialogData)
        );

        const required = this.validate(this.state.dialogData);
        if (required.length) {
            this.setState({ alertMessages: required });
            return;
        }

        const dialogData = { ...this.state.dialogData };
        Object.keys(dialogData).forEach((key) => {
            if (this.props.Head.find((field) => field.id === key)?.onlyRead) {
                delete dialogData[key];
            }
        });

        // Fields marked `json` are typed as text and sent as objects. The core
        // wants a real nested object for receiverInfo/senderInfo; a string would
        // come back as a type mismatch.
        const jsonFields = this.props.Head.filter((field) => field.json);
        for (let i = 0; i < jsonFields.length; i += 1) {
            const field = jsonFields[i];
            const raw = dialogData[field.id];
            if (raw === '' || raw === null || raw === undefined) {
                delete dialogData[field.id];
            } else if (typeof raw === 'string') {
                try {
                    dialogData[field.id] = JSON.parse(raw);
                } catch (parseError) {
                    this.setState({
                        loading: false,
                        alertMessages: [
                            `${i18n.t(`columns.${field.label}`)}: ${i18n.t('errors.invalidJson')}`
                        ]
                    });
                    return;
                }
            }
        }

        this.setState({ loading: true, alertMessages: [] });
        try {
            const result = await this.props.server(this.props.id, dialogData);
            if (!hasError(result)) {
                if (this.props.onSuccess) {
                    // The caller reports the outcome itself: a decision on an
                    // appeal has no transaction to show.
                    this.props.onSuccess(result.data);
                    this.handleClose();
                } else if (result.data && typeof result.data === 'string') {
                    this.handleShowLink(result.data);
                } else if (result.data && typeof result.data === 'object') {
                    // Created transactions come back as an object. Closing here —
                    // which is what this used to do — would throw away the uuid and
                    // the payment instructions, so there would be nothing on screen
                    // to say anything had happened.
                    this.setState({ result: result.data, alertMessages: [] });
                } else {
                    this.handleClose();
                }
            } else {
                this.setState({ alertMessages: formatApiErrors(result) });
            }
        } catch (err) {
            console.error('Save error:', err);
            this.setState({ alertMessages: [i18n.t('text.unexpectedError')] });
        } finally {
            this.setState({ loading: false });
        }
    };

    handleValueChanged = (id, value) => {
        this.setState((prevState) => ({
            dialogData: {
                ...prevState.dialogData,
                [id]: value
            },
            prevDataLoaded: false
        }));
    };

    fillPrevDialogData = () => {
        if (!this.props.enablePrevData) return;
        const prevData = localStorage.getItem(
            `lastDialogData_${this.props.id}_${this.type}`
        );
        if (prevData) {
            this.setState({
                dialogData: JSON.parse(prevData),
                alertMessages: [],
                alertTitle: '',
                prevDataLoaded: true,
                hasPrevData: true
            });
        } else {
            this.initializeDialogData();
        }
    };

    copyUuid = (uuid) => {
        navigator.clipboard?.writeText(uuid);
        this.setState({ copied: true });
    };

    renderRow(labelKey, value) {
        if (value === null || value === undefined || value === '') return null;
        return (
            <Stack direction="row" spacing={1} justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                    {i18n.t(`columns.${labelKey}`)}
                </Typography>
                <Typography variant="body2">{value}</Typography>
            </Stack>
        );
    }

    renderResult(result) {
        const qrValue = QR_KEYS.map((key) => result.receiverInfo?.[key]).find(
            (value) => typeof value === 'string' && value.length > 0
        );

        return (
            <Stack spacing={2}>
                <Alert severity="success">
                    <AlertTitle>{i18n.t('text.success')}</AlertTitle>
                    {i18n.t('text.transactionCreated')}
                </Alert>

                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                        {i18n.t('columns.uuid')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {result.uuid}
                    </Typography>
                    <Tooltip
                        title={
                            this.state.copied
                                ? i18n.t('text.copied')
                                : i18n.t('text.copy')
                        }
                    >
                        <IconButton
                            size="small"
                            onClick={() => this.copyUuid(result.uuid)}
                        >
                            <Iconify icon="eva:copy-fill" width={16} />
                        </IconButton>
                    </Tooltip>
                </Stack>

                {result.state && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                            {i18n.t('columns.state')}
                        </Typography>
                        <Label {...statusLabelProps(String(result.state).toLowerCase())}>
                            {i18n.t(`enums.${String(result.state).toLowerCase()}`)}
                        </Label>
                    </Stack>
                )}

                {this.renderRow('requestedAmount', centToCurrency(result.requestedAmount))}
                {/* Payins on a gateway sharing wallets by amount: the payer must send exactly this. */}
                {result.payableAmount != null && this.renderRow('payableAmount', centToCurrency(result.payableAmount))}
                {this.renderRow('actualAmount', centToCurrency(result.actualAmount))}
                {this.renderRow('walletAmount', centToCurrency(result.walletAmount))}
                {this.renderRow('merchantTransactionId', result.merchantTransactionId)}
                {this.renderRow('gatewayTransactionId', result.gatewayTransactionId)}
                {this.renderRow('stateMessage', result.stateMessage)}

                {result.receiverInfo && Object.keys(result.receiverInfo).length > 0 && (
                    <Stack spacing={1}>
                        <Typography variant="subtitle2">
                            {i18n.t('text.paymentInstructions')}
                        </Typography>
                        {qrValue && (
                            <Box sx={{ alignSelf: 'center', bgcolor: '#fff', p: 2 }}>
                                <QRCode value={qrValue} size={160} />
                            </Box>
                        )}
                        <JsonView src={result.receiverInfo} collapsed={1} />
                    </Stack>
                )}
            </Stack>
        );
    }

    render() {
        const {
            dialogOpen,
            onClose,
            Head = [],
            dialogTitle,
            saveLabel,
            cancelLabel
        } = this.props;

        const { dialogData, alertMessages, alertTitle, loading, link, result } =
            this.state;

        const finished = Boolean(link || result);

        return (
            <FormDialog open={dialogOpen} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        {dialogTitle
                            ? i18n.t(dialogTitle)
                            : i18n.t('dialogs.create')}
                        {this.props.enablePrevData && !finished && (
                            <Button
                                variant="text"
                                onClick={this.fillPrevDialogData}
                                disabled={
                                    !this.state.hasPrevData ||
                                    this.state.prevDataLoaded
                                }
                            >
                                {i18n.t('buttons.previousData')}
                            </Button>
                        )}
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {alertMessages.length > 0 && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            <AlertTitle>
                                {alertTitle || i18n.t('text.error')}
                            </AlertTitle>
                            {/* Not a <ul>: global styles zero out list padding, so
                                the markers were clipped. Same shape DialogAbstract
                                uses for its validation list. */}
                            <Stack component="span" spacing={0.5}>
                                {alertMessages.map((message, index) => (
                                    <Typography
                                        component="span"
                                        variant="body2"
                                        key={index}
                                    >
                                        {message}
                                    </Typography>
                                ))}
                            </Stack>
                        </Alert>
                    )}

                    {link && (
                        <Alert severity="success">
                            <AlertTitle>{i18n.t('text.success')}</AlertTitle>
                            {i18n.t('text.orderCreated')}
                            <br />
                            <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {link}
                            </a>
                        </Alert>
                    )}

                    {result && this.renderResult(result)}

                    {!finished && Array.isArray(Head) && (
                        // One spacing rhythm, owned by the container — the fields
                        // bring none of their own and used to sit flush together.
                        <Stack spacing={2.5} sx={{ mt: 1 }}>
                            {Head.map((field) => {
                                const Field = Fields[field.cell] || Fields.Text;

                                return (
                                    <Field
                                        key={field.id}
                                        field={field}
                                        value={dialogData?.[field.id]}
                                        // DialogAmountField reads data.currency to
                                        // pick its currency symbol.
                                        data={dialogData}
                                        onChange={this.handleValueChanged}
                                    />
                                );
                            })}
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions>
                    {finished ? (
                        <Button
                            color="inherit"
                            onClick={() => this.handleClose()}
                        >
                            {i18n.t('buttons.close')}
                        </Button>
                    ) : (
                        <>
                            <Button
                                color="inherit"
                                disabled={loading}
                                onClick={onClose}
                            >
                                {cancelLabel
                                    ? i18n.t(cancelLabel)
                                    : i18n.t('buttons.cancel')}
                            </Button>
                            <LoadingButton
                                variant="contained"
                                loading={loading}
                                onClick={this.handleDialogSave}
                            >
                                {saveLabel
                                    ? i18n.t(saveLabel)
                                    : i18n.t('buttons.save')}
                            </LoadingButton>
                        </>
                    )}
                </DialogActions>
            </FormDialog>
        );
    }
}

CreateTransactionDialog.propTypes = {
    dialogOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    Head: PropTypes.arrayOf(PropTypes.object).isRequired,
    server: PropTypes.func.isRequired,
    id: PropTypes.any.isRequired,
    // Separates one dialog's "previous data" cache from another's on the same row.
    type: PropTypes.string,
    dialogTitle: PropTypes.string,
    saveLabel: PropTypes.string,
    cancelLabel: PropTypes.string,
    enablePrevData: PropTypes.bool,
    // Given, a success closes the dialog and hands the response's data here
    // rather than showing it as a created transaction.
    onSuccess: PropTypes.func
};
CreateTransactionDialog.defaultProps = {
    enablePrevData: true
};
